import React from 'react';
import { constants, verifiers } from '@yola/subscription-manager-js';
import { t } from '../../localization';
import { PER_YEAR } from '../../common/constants/periods';
import planFeaturesGroupIds from '../constants/plan-features-gruop-ids';

const { hostingPackageFeatureNames: features } = constants;

const MEGABYTES_IN_GIGABYTE = 1024;
const mb2gb = (mb) => (mb / MEGABYTES_IN_GIGABYTE).toFixed();

const featureConfigurator = {
  [features.SITE_COUNT]: {
    config(availablePlans) {
      return {
        id: features.SITE_COUNT,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Number of websites'),
        tooltip: t('Create numerous websites under one account.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: `${config[features.SITE_COUNT]}`,
          compact: `${t('Number of websites')}: ${config[features.SITE_COUNT]}`,
        })),
      };
    },
  },
  [features.SITE_STORAGE_QUOTA]: {
    config(availablePlans, options) {
      const { isB2C } = options;
      const tooltip = isB2C
        ? t(
            'Get the storage you need to upload more images, videos, audio, and documents to your Yola site'
          )
        : t(
            'Get the storage you need to upload more images, videos, audio, and documents to your site'
          );

      return {
        id: features.SITE_STORAGE_QUOTA,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Storage space'),
        tooltip,
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => {
          const gigabytes = mb2gb(config[features.SITE_STORAGE_QUOTA]);
          let text;

          if (gigabytes < 1) {
            text = t('{megabytes} MB', {
              megabytes: config[features.SITE_STORAGE_QUOTA],
            });
          } else if (gigabytes >= 10) {
            text = t('Unlimited');
          } else {
            text = t('{gigabytes} GB', {
              gigabytes,
            });
          }

          return {
            id,
            contentType: 'text',
            text,
            compact: `${t('Storage space')}: ${text}`,
          };
        }),
      };
    },
  },
  [features.PARTNER_ADS]: {
    config(availablePlans, options) {
      const { isB2C } = options;
      const title = isB2C ? t('Remove Yola Ads') : t('Remove Ads');
      const tooltip = isB2C
        ? t(
            "Remove all Yola ads & give your visitors a site experience that's entirely your own brand."
          )
        : t(
            "Remove all ads & give your visitors a site experience that's entirely your own brand."
          );

      return {
        id: features.PARTNER_ADS,
        groupId: planFeaturesGroupIds.CORE,
        title,
        tooltip,
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.PARTNER_ADS] ? 'icon-minus' : 'icon-check',
          compact: config[features.PARTNER_ADS] ? null : title,
        })),
      };
    },
  },
  [features.CUSTOM_CODE]: {
    config(availablePlans) {
      return {
        id: features.CUSTOM_CODE,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Custom code editing'),
        tooltip: t('Complete site customization with CSS and JavaScript.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.CUSTOM_CODE] ? 'icon-check' : 'icon-minus',
          compact: config[features.CUSTOM_CODE] ? t('Custom code editing') : null,
        })),
      };
    },
  },
  [features.FREE_DOMAIN_PROMO]: {
    config(availablePlans, options) {
      const { paymentPeriod, highlightDisabledOnly } = options;
      const disabled = paymentPeriod !== PER_YEAR;

      return {
        id: features.FREE_DOMAIN_PROMO,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Free one-year domain'),
        tooltip: (
          <span>
            {t('Claim a free domain for 1 year with the following extensions:')}
            <strong>
              .com, .uk, .co.uk, .us, .it, .biz, .de, .nl, .eu, .in, .co.za, .club, .info, .work,
              .xyz
            </strong>
            <br />
            {t('OR get a discount for any other extension')}
          </span>
        ),
        highlighted: disabled && highlightDisabledOnly,
        availabilities: availablePlans.map(({ id, config }) => {
          let contentType = 'icon-minus';

          if (config[features.FREE_DOMAIN_PROMO] && !disabled) {
            contentType = 'icon-check';
          } else if (!verifiers.isFreeHostingPackageType(id)) {
            contentType = 'icon-not-available';
          }

          return {
            id,
            contentType,
            compact: config[features.FREE_DOMAIN_PROMO] ? t('Free one-year domain') : null,
          };
        }),
      };
    },
  },
  [features.CUSTOM_DOMAIN]: {
    config(availablePlans) {
      return {
        id: features.CUSTOM_DOMAIN,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Publish to a custom domain'),
        tooltip: t(
          'Connect a custom domain, look professional online & build your brand credibility.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.CUSTOM_DOMAIN] ? 'icon-check' : 'icon-minus',
          compact: config[features.CUSTOM_DOMAIN] ? t('Publish to a custom domain') : null,
        })),
      };
    },
  },
  [features.PRIVATE_REGISTRATION]: {
    config(availablePlans) {
      return {
        id: features.PRIVATE_REGISTRATION,
        groupId: planFeaturesGroupIds.SECURITY,
        title: t('Free Domain WHOIS Privacy Protection'),
        tooltip: t(
          'Get private WHOIS registration to your custom domain to safeguard your contact information and protect yourself from spammers and telemarketers.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.PRIVATE_REGISTRATION] ? 'icon-check' : 'icon-minus',
          compact: config[features.PRIVATE_REGISTRATION]
            ? t('Free Domain WHOIS Privacy Protection')
            : null,
        })),
      };
    },
  },
  [features.PREMIUM_BLOCKS_AND_WIDGETS]: {
    config(availablePlans) {
      return {
        id: features.PREMIUM_BLOCKS_AND_WIDGETS,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Premium blocks and widgets'),
        tooltip: t(
          'Unlock exclusive blocks to enhance your design options and create a one-of-a-kind website.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: `${config[features.PREMIUM_BLOCKS_AND_WIDGETS]}`,
          compact: `${t('Premium blocks and widgets')}: ${
            config[features.PREMIUM_BLOCKS_AND_WIDGETS]
          }`,
        })),
      };
    },
  },
  [features.ADVANCED_COLOR_CUSTOMIZATION]: {
    config(availablePlans) {
      return {
        id: features.ADVANCED_COLOR_CUSTOMIZATION,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Advanced website customization'),
        tooltip: t(
          'Personalize colors for each element effortlessly with our deep customization feature.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.ADVANCED_COLOR_CUSTOMIZATION] ? 'icon-check' : 'icon-minus',
          compact: config[features.ADVANCED_COLOR_CUSTOMIZATION]
            ? t('Advanced website customization')
            : null,
        })),
      };
    },
  },
  [features.PASSWORD_PROTECTED_PAGES]: {
    config(availablePlans) {
      return {
        id: features.PASSWORD_PROTECTED_PAGES,
        groupId: planFeaturesGroupIds.SECURITY,
        title: t('Page password protection'),
        tooltip: t(
          'Set passwords to control access to specific pages on your website, ensuring only authorized users can view restricted content.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.PASSWORD_PROTECTED_PAGES] ? 'icon-check' : 'icon-minus',
          compact: config[features.PASSWORD_PROTECTED_PAGES] ? t('Page password protection') : null,
        })),
      };
    },
  },
  [features.PREMIUM_ICONS_PAYWALL]: {
    config(availablePlans) {
      return {
        id: features.PREMIUM_ICONS_PAYWALL,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Premium icons'),
        tooltip: t(
          'Enhance your design with our advanced icon gallery featuring over 1.2 million icons.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => {
          const iconsCount = config[features.PREMIUM_ICONS_PAYWALL];
          const text = Number.isInteger(iconsCount)
            ? iconsCount
            : t('{iconsCount} million', {
                iconsCount,
              });
          return {
            id,
            contentType: 'text',
            text,
            compact: `${t('Premium icons')}: ${text}`,
          };
        }),
      };
    },
  },
  [features.UPLOAD_SIZE]: {
    config(availablePlans) {
      return {
        id: features.UPLOAD_SIZE,
        groupId: planFeaturesGroupIds.CORE,
        title: t('File size limit'),
        tooltip: t('Choose the right plan to upload larger files to your site.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: t('{megabytes} MB', {
            megabytes: config[features.UPLOAD_SIZE],
          }),
          compact: `${t('File size limit')}: ${t('{megabytes} MB', {
            megabytes: config[features.UPLOAD_SIZE],
          })}`,
        })),
      };
    },
  },
  [features.PAGE_COUNT]: {
    config(availablePlans) {
      return {
        id: features.PAGE_COUNT,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Pages on website'),
        tooltip: t('Choose the right plan to have the number of pages you need.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: `${config[features.PAGE_COUNT]}`,
          compact: `${t('Pages on website')}: ${config[features.PAGE_COUNT]}`,
        })),
      };
    },
  },
  [features.ANALYTICS]: {
    config(availablePlans) {
      return {
        id: features.ANALYTICS,
        groupId: planFeaturesGroupIds.MARKETING,
        title: t('Visitors Analytics'),
        tooltip: t(
          'Gain insights and stats about your site, including visitor origins and top-performing pages.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.ANALYTICS] ? 'icon-check' : 'icon-minus',
          compact: config[features.ANALYTICS] ? t('Visitors Analytics') : null,
        })),
      };
    },
  },
  [features.MULTILANGUAGE_SITE]: {
    config(availablePlans) {
      return {
        id: features.MULTILANGUAGE_SITE,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Multilanguage site'),
        tooltip: t(
          'Create multilingual websites to attract visitors from different languages and regions.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.MULTILANGUAGE_SITE] ? 'icon-check' : 'icon-minus',
          compact: config[features.MULTILANGUAGE_SITE] ? t('Multilanguage site') : null,
        })),
      };
    },
  },
  [features.SITE_AUTO_BACKUPS]: {
    config(availablePlans) {
      return {
        id: features.SITE_AUTO_BACKUPS,
        groupId: planFeaturesGroupIds.SECURITY,
        title: t('Automatic site backups'),
        tooltip: t(
          'Protect your data with automatic site backups, ensuring quick recovery, minimal downtime, and easy version control.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.SITE_AUTO_BACKUPS] ? 'icon-check' : 'icon-minus',
          compact: config[features.SITE_AUTO_BACKUPS] ? t('Automatic site backups') : null,
        })),
      };
    },
  },
  [features.TRAFFIC_BUILDER]: {
    config(availablePlans) {
      return {
        id: features.TRAFFIC_BUILDER,
        groupId: planFeaturesGroupIds.SEO,
        title: t('Automatic SEO monitoring'),
        tooltip: t('Get automatic SEO monitoring to analyze and grow your traffic.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.TRAFFIC_BUILDER] ? 'icon-check' : 'icon-minus',
          compact: config[features.TRAFFIC_BUILDER] ? t('Automatic SEO monitoring') : null,
        })),
      };
    },
  },
  [features.CUSTOMER_CARE]: {
    config(availablePlans) {
      return {
        id: features.CUSTOMER_CARE,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Customer care'),
        tooltip: t('Enjoy 24/7 customer support. Upgrade to a higher plan for priority support.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => {
          const customerCareNames = {
            limited: t('Limited Customer Care'),
            standard: t('24/7 Customer Care'),
            priority: t('Priority Customer Care'),
          };

          return {
            id,
            contentType: 'text',
            text: customerCareNames[config[features.CUSTOMER_CARE]] || '',
            compact: customerCareNames[config[features.CUSTOMER_CARE]] || null,
          };
        }),
      };
    },
  },
  [features.AI]: {
    config(availablePlans, options) {
      const { isB2C } = options;
      const title = isB2C ? t('Yola AI') : t('AI');
      const tooltip = isB2C
        ? t('Use Yola Artificial Intelligence (AI) for effortless website creation.')
        : t('Use Artificial Intelligence (AI) for effortless website creation.');

      return {
        id: features.AI,
        groupId: planFeaturesGroupIds.CORE,
        title,
        tooltip,
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => {
          const aiNames = {
            basic: t('Basic'),
            advanced: t('Advanced'),
          };

          return {
            id,
            contentType: 'text',
            text: aiNames[config[features.AI]],
            compact: `${title}: ${aiNames[config[features.AI]]}`,
          };
        }),
      };
    },
  },
  [features.PREMIUM_STOSK_IMAGES]: {
    config(availablePlans) {
      return {
        id: features.PREMIUM_STOSK_IMAGES,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Premium Stock images'),
        tooltip: t(
          'Enhance your site using over 10 million premium stock images available at no cost.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: t('{iconsCount} million', {
            iconsCount: config[features.PREMIUM_STOSK_IMAGES],
          }),
          compact: `${t('Premium Stock images')}: ${t('{iconsCount} million', {
            iconsCount: config[features.PREMIUM_STOSK_IMAGES],
          })}`,
        })),
      };
    },
  },
  [features.PREMIUM_WEBSITE_TEMPLATES]: {
    config(availablePlans) {
      return {
        id: features.PREMIUM_WEBSITE_TEMPLATES,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Premium website templates'),
        tooltip: t(
          'Choose from 85+ website templates to start editing and adding your unique content.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: `${config[features.PREMIUM_WEBSITE_TEMPLATES]}`,
          compact: `${t('Premium website templates')}: ${
            config[features.PREMIUM_WEBSITE_TEMPLATES]
          }`,
        })),
      };
    },
  },

  [features.PREMIUM_TEXT_DECORATIONS]: {
    config(availablePlans) {
      return {
        id: features.PREMIUM_TEXT_DECORATIONS,
        groupId: planFeaturesGroupIds.CORE,
        title: t('Advanced Text Formatting'),
        tooltip: t(
          'Enhance your text with versatile, dynamic styling options to improve readability and visual appeal.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.PREMIUM_TEXT_DECORATIONS] ? 'icon-check' : 'icon-minus',
          compact: config[features.PREMIUM_TEXT_DECORATIONS] ? t('Advanced Text Formatting') : null,
        })),
      };
    },
  },

  [features.LEAD_CAPTURE_FORMS]: {
    config(availablePlans) {
      return {
        id: features.LEAD_CAPTURE_FORMS,
        groupId: planFeaturesGroupIds.MARKETING,
        title: t('Lead-capture forms'),
        tooltip: t(
          'Easily create and customize forms to gather contact information from your site visitors, helping you generate leads and grow your audience.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.LEAD_CAPTURE_FORMS] ? 'icon-check' : 'icon-minus',
          compact: config[features.LEAD_CAPTURE_FORMS] ? t('Lead-capture forms') : null,
        })),
      };
    },
  },
  [features.FIELDS_PER_FORM]: {
    config(availablePlans) {
      return {
        id: features.FIELDS_PER_FORM,
        groupId: planFeaturesGroupIds.MARKETING,
        title: t('Fields per form'),
        tooltip: t(
          'Add unlimited fields to your lead-capture forms to collect all necessary information from site visitors.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => {
          const fieldsPerFormNames = {
            unlimited: t('Unlimited'),
          };

          return {
            id,
            contentType: 'text',
            text: fieldsPerFormNames[config[features.FIELDS_PER_FORM]],
            compact: `${t('Form submissions')}: ${
              fieldsPerFormNames[config[features.FIELDS_PER_FORM]]
            }`,
          };
        }),
      };
    },
  },
  [features.FORM_SUBMISSIONS]: {
    config(availablePlans) {
      return {
        id: features.FORM_SUBMISSIONS,
        groupId: planFeaturesGroupIds.MARKETING,
        title: t('Form submissions'),
        tooltip: t(
          'Capture all your site’s leads with unlimited form submissions at no extra cost.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => {
          const formSubmissionsNames = {
            unlimited: t('Unlimited'),
          };

          return {
            id,
            contentType: 'text',
            text: formSubmissionsNames[config[features.FORM_SUBMISSIONS]],
            compact: `${t('Form submissions')}: ${
              formSubmissionsNames[config[features.FORM_SUBMISSIONS]]
            }`,
          };
        }),
      };
    },
  },
  [features.AI_SITE_WIZARD]: {
    config(availablePlans) {
      return {
        id: features.AI_SITE_WIZARD,
        groupId: planFeaturesGroupIds.AI,
        title: t('AI Website Builder'),
        tooltip: t(
          'Overcome writer’s block with an AI-generated website that you can easily refine to meet your needs.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.AI_SITE_WIZARD] ? 'icon-check' : 'icon-minus',
          compact: config[features.AI_SITE_WIZARD] ? t('AI Website Builder') : null,
        })),
      };
    },
  },
  [features.AI_DESIGN_ASSISTANT]: {
    config(availablePlans) {
      return {
        id: features.AI_DESIGN_ASSISTANT,
        groupId: planFeaturesGroupIds.AI,
        title: t('AI Design Assistant'),
        tooltip: t(
          'Let AI handle design decisions to ensure a consistent and compelling look for your website.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.AI_DESIGN_ASSISTANT] ? 'icon-check' : 'icon-minus',
          compact: config[features.AI_DESIGN_ASSISTANT] ? t('AI Design Assistant') : null,
        })),
      };
    },
  },
  [features.AI_BLOCK_GENERATOR]: {
    config(availablePlans) {
      return {
        id: features.AI_BLOCK_GENERATOR,
        groupId: planFeaturesGroupIds.AI,
        title: t('AI Block Assistant'),
        tooltip: t(
          'Accelerate content creation with AI for rapid generation and regeneration of copy, images, and layouts.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.AI_BLOCK_GENERATOR] ? 'icon-check' : 'icon-minus',
          compact: config[features.AI_BLOCK_GENERATOR] ? t('AI Block Assistant') : null,
        })),
      };
    },
  },
  [features.AI_TEXT_ASSISTANT]: {
    config(availablePlans) {
      return {
        id: features.AI_TEXT_ASSISTANT,
        groupId: planFeaturesGroupIds.AI,
        title: t('AI Text Assistant'),
        tooltip: t(
          'Make precise, targeted adjustments to your site’s content with AI, ensuring your messaging is always on point.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.AI_TEXT_ASSISTANT] ? 'icon-check' : 'icon-minus',
          compact: config[features.AI_TEXT_ASSISTANT] ? t('AI Text Assistant') : null,
        })),
      };
    },
  },
  [features.SEARCH_ENGINE_OPTIMIZATION]: {
    config(availablePlans) {
      return {
        id: features.SEARCH_ENGINE_OPTIMIZATION,
        groupId: planFeaturesGroupIds.SEO,
        title: t('Search Engine Optimization'),
        tooltip: t(
          'Attract more users and leads from Google with a fast, secure website that boosts your SEO rankings.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.SEARCH_ENGINE_OPTIMIZATION] ? 'icon-check' : 'icon-minus',
          compact: config[features.SEARCH_ENGINE_OPTIMIZATION]
            ? t('Search Engine Optimization')
            : null,
        })),
      };
    },
  },
  [features.AUTOMATIC_SITE_SPEED_OPTIMIZATION]: {
    config(availablePlans) {
      return {
        id: features.AUTOMATIC_SITE_SPEED_OPTIMIZATION,
        groupId: planFeaturesGroupIds.SEO,
        title: t('Automatic Site Speed optimization'),
        tooltip: (
          <>
            <span>
              {t(
                'Automatically optimize both the frontend and backend of your website with features like:'
              )}
            </span>
            <ul style={{ paddingLeft: '16px' }}>
              <li>{t('CSS & HTML minification and compression')}</li>
              <li>{t('JS minification and compression')}</li>
              <li>{t('Image lazy loading')}</li>
              <li>{t('Font swap')}</li>
              <li>{t('iFrame and video lazy loading')}</li>
            </ul>
          </>
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.AUTOMATIC_SITE_SPEED_OPTIMIZATION]
            ? 'icon-check'
            : 'icon-minus',
          compact: config[features.AUTOMATIC_SITE_SPEED_OPTIMIZATION]
            ? t('Automatic Site Speed optimization')
            : null,
        })),
      };
    },
  },
  [features.AUTOMATIC_IMAGE_OPTIMIZATION]: {
    config(availablePlans) {
      return {
        id: features.AUTOMATIC_IMAGE_OPTIMIZATION,
        groupId: planFeaturesGroupIds.SEO,
        title: t('Automatic Image optimization'),
        tooltip: (
          <>
            <span>{t('Automatically optimize your website’s images by:')}</span>
            <ul style={{ paddingLeft: '16px' }}>
              <li>{t('Converting images to WebP')}</li>
              <li>{t('Container-specific image resizing')}</li>
              <li>{t('Applying lossy image compression')}</li>
            </ul>
          </>
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.AUTOMATIC_IMAGE_OPTIMIZATION] ? 'icon-check' : 'icon-minus',
          compact: config[features.AUTOMATIC_IMAGE_OPTIMIZATION]
            ? t('Automatic Image optimization')
            : null,
        })),
      };
    },
  },
  [features.REDIRECTS]: {
    config(availablePlans) {
      return {
        id: features.REDIRECTS,
        groupId: planFeaturesGroupIds.SEO,
        title: t('301 redirects'),
        tooltip: t(
          'Redirect traffic to ensure visitors find the right content and maintain your SEO rankings.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.REDIRECTS] ? 'icon-check' : 'icon-minus',
          compact: config[features.REDIRECTS] ? t('301 redirects') : null,
        })),
      };
    },
  },
  [features.SITEMAP_CONTROLS]: {
    config(availablePlans) {
      return {
        id: features.SITEMAP_CONTROLS,
        groupId: planFeaturesGroupIds.SEO,
        title: t('Sitemap controls'),
        tooltip: t(
          'Automatically generate an XML sitemap to help Google find and crawl your pages.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.SITEMAP_CONTROLS] ? 'icon-check' : 'icon-minus',
          compact: config[features.SITEMAP_CONTROLS] ? t('Sitemap controls') : null,
        })),
      };
    },
  },
  [features.DDOS_PROTECTION]: {
    config(availablePlans) {
      return {
        id: features.DDOS_PROTECTION,
        groupId: planFeaturesGroupIds.SECURITY,
        title: t('DDoS protection'),
        tooltip: t(
          'Safeguard your website with advanced DDoS protection to prevent attacks and ensure uninterrupted access for your visitors.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.DDOS_PROTECTION] ? 'icon-check' : 'icon-minus',
          compact: config[features.DDOS_PROTECTION] ? t('DDoS protection') : null,
        })),
      };
    },
  },
  [features.GDRP_AND_CCPA_COMPLIANCE]: {
    config(availablePlans) {
      return {
        id: features.GDRP_AND_CCPA_COMPLIANCE,
        groupId: planFeaturesGroupIds.SECURITY,
        title: t('GDPR & CCPA compliance'),
        tooltip: t(
          'Ensure GDPR and CCPA compliance for cookies and online tracking with Cookiebot Consent Management Platform (CMP) integration.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.GDRP_AND_CCPA_COMPLIANCE] ? 'icon-check' : 'icon-minus',
          compact: config[features.GDRP_AND_CCPA_COMPLIANCE] ? t('GDPR & CCPA compliance') : null,
        })),
      };
    },
  },
  [features.FREE_UNLIMITED_SSL]: {
    config(availablePlans) {
      return {
        id: features.FREE_UNLIMITED_SSL,
        groupId: planFeaturesGroupIds.SECURITY,
        title: t('Free unlimited SSL'),
        tooltip: t(
          'Automatically configure free SSL certificates for all websites, including those with custom domains. SSL-secured sites achieve higher rankings on Google.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.FREE_UNLIMITED_SSL] ? 'icon-check' : 'icon-minus',
          compact: config[features.FREE_UNLIMITED_SSL] ? t('Free unlimited SSL') : null,
        })),
      };
    },
  },
  [features.WEBSITE_EXPORT]: {
    config(availablePlans) {
      return {
        id: features.WEBSITE_EXPORT,
        groupId: planFeaturesGroupIds.MORE,
        title: t('Website export'),
        tooltip: t(
          'Export and download a zip file containing all images, files, CSS, HTML, and JavaScript from your website.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[features.WEBSITE_EXPORT] ? 'icon-check' : 'icon-minus',
          compact: config[features.WEBSITE_EXPORT] ? t('Website export') : null,
        })),
      };
    },
  },
};

export default featureConfigurator;
